import React, { useEffect, useState, useMemo } from 'react';
import { StrictFetcher } from '@this/src/util';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';

import HotelList from '@this/domain/hotel/hotel_list';
import FlightList from '@this/domain/flight/flight_list';
import TransitList from '@this/domain/transit/transit_list';
import User from '@this/domain/user/user';
import type UserJson from '@this/domain/user/user_json';
import type SelectStore from '@this/src/domain/select_store';
import type SelectRepository from '@this/src/domain/select_repository';
import type SearchQueryItem from '@this/src/domain/search_query_item';
import type SearchResultItem from '@this/src/domain/search_result_item';

import type SearchQuery from '@this/domain/search_query';
import SelectListAreaSearchTemplate from '../list_area_search/list_area_search.template';
import HotelListAreaContent from '../HotelListAreaContent';
import TransportListArea from './TransportListArea';
import FlightListArea from './FlightListArea';

interface Props {
  query: SearchQuery;
  store: SelectStore;
  repository: SelectRepository;
  serviceId: number;
  icon: string;
  listWrapper: TransitList | HotelList | FlightList | null;
  type: string | undefined;
  subtype: number | undefined;
  show: boolean;
  hotelPriceLimit: number | null;
  queryItem?: SearchQueryItem;
  resultItem?: SearchResultItem;
  direction?: string;
  showSearchBox: boolean;
  toggleShowSearchBox: () => void;
  transportSegment: number | undefined;
  isOpenFlightCondition: boolean;
  errors: string[];
  handleTransportSegmentClick: (i: number) => () => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  setItemLoading: (loading: boolean) => void;
  handleFlightConditionSubmit: (
    filteredUpgradeSeat: boolean,
    carrierIds: string[],
    changeablePrices: string[],
    showConnectingAir: boolean
  ) => void;
  openFlightCondition: () => void;
  closeFlightCondition: () => void;
  isPcLayout?: boolean;
}
const ListAreaShortDistanceContainer: React.FC<Props> = props => {
  const {
    query,
    store,
    repository,
    serviceId,
    icon,
    listWrapper,
    type,
    subtype,
    show,
    queryItem,
    resultItem,
    direction,
    transportSegment,
    errors,
    showSearchBox,
    handleTransportSegmentClick,
    toggleShowSearchBox,
    handleSubmit,
    setItemLoading,
    handleFlightConditionSubmit,
    openFlightCondition,
    closeFlightCondition,
    isOpenFlightCondition,
    isPcLayout = false
  } = props;
  const [shownFlightFilter, setShownFlightFilter] = useState(false);
  const [me, setMe] = useState<User | null>(null);
  const { theme } = useAITTheme();
  const styles = useMemo(() => getListAreaStyles(theme, show, isPcLayout), [theme, show, isPcLayout]);

  useEffect(() => {
    StrictFetcher.get<UserJson>('/users.json', 'users/index.json').then(
      response => {
        setMe(new User(response));
      },
      () => {
        setMe(null);
      }
    );
  }, []);

  // ホテル専用のレンダリング関数
  const renderHotelListArea = () => {
    if (!(listWrapper instanceof HotelList) || resultItem == null) {
      return null;
    }

    const searchArea =
      store.result.type === 'separate' &&
      (store.result.isRoundTripSearchResult() || store.result.isMultipleLocationsSearchResult()) &&
      store.result.items.length > 0 &&
      query &&
      queryItem ? (
        <SelectListAreaSearchTemplate
          showSearchBox={showSearchBox}
          toggleShowSearchBox={toggleShowSearchBox}
          handleSubmit={handleSubmit}
          queryItem={queryItem}
          query={query}
          errors={errors}
        />
      ) : null;

    const hotelContent = (
      <HotelListAreaContent
        listWrapper={listWrapper}
        resultItem={resultItem}
        icon={icon}
        serviceId={serviceId}
        store={store}
        isMobile={!isPcLayout}
        isPcLayout={isPcLayout}
        showFilter={false}
        me={me}
        repository={repository}
      />
    );

    if (isPcLayout) {
      return (
        <div style={styles.listContent}>
          {searchArea && <div style={styles.searchArea}>{searchArea}</div>}
          {hotelContent}
        </div>
      );
    }

    return (
      <>
        {searchArea}
        {hotelContent}
      </>
    );
  };

  // 海外航空券専用のレンダリング関数
  const renderFlightListArea = () => {
    if (!(listWrapper instanceof FlightList) || subtype === undefined) {
      return null;
    }

    return (
      <FlightListArea
        store={store}
        repository={repository}
        icon={icon}
        listWrapper={listWrapper}
        subtype={subtype}
        queryItem={queryItem}
        resultItem={resultItem}
        shownFlightFilter={shownFlightFilter}
        setShownFlightFilter={setShownFlightFilter}
      />
    );
  };

  // 交通機関専用のレンダリング関数
  const renderTransportListArea = () => {
    if (!(listWrapper instanceof TransitList)) {
      return null;
    }

    return (
      <TransportListArea
        query={query}
        store={store}
        repository={repository}
        icon={icon}
        listWrapper={listWrapper}
        queryItem={queryItem}
        direction={direction}
        showSearchBox={showSearchBox}
        toggleShowSearchBox={toggleShowSearchBox}
        transportSegment={transportSegment}
        isOpenFlightCondition={isOpenFlightCondition}
        errors={errors}
        handleTransportSegmentClick={handleTransportSegmentClick}
        handleSubmit={handleSubmit}
        setItemLoading={setItemLoading}
        handleFlightConditionSubmit={handleFlightConditionSubmit}
        openFlightCondition={openFlightCondition}
        closeFlightCondition={closeFlightCondition}
        isPcLayout={isPcLayout}
      />
    );
  };

  // type別にレンダリング関数を呼び分け
  const renderContent = () => {
    // 共通のLoading処理
    if (resultItem?.loading) {
      return (
        <>
          <img className="select__loading" src="/images/loading.gif" width={50} height={50} alt="読み込み中" />
          {resultItem.selectedLoading === false && (
            <div className="select__loading-text">
              {type === 'hotel' ? 'ホテル' : type === 'transport' ? '経路' : '航空券'}を検索中です
            </div>
          )}
        </>
      );
    }

    switch (type) {
      case 'hotel':
        return renderHotelListArea();
      case 'transport':
        return renderTransportListArea();
      case 'flight':
        return renderFlightListArea();
      default:
        return null;
    }
  };

  return (
    <div style={styles.listArea} className={`select-list-area ${show ? '' : 'hidden'}`}>
      {renderContent()}
    </div>
  );
};

const getListAreaStyles = (theme: AITTheme, show: boolean, isPcLayout: boolean) => {
  const isEditPage = location.pathname.match(/trips\/\d+\/edit/);
  const backgroundColor = isEditPage ? '#fff' : theme.accentColorLight;

  if (!isPcLayout) {
    return {
      listArea: {
        flexGrow: 99999,
        background: backgroundColor,
        padding: '10px',
        position: 'relative' as const,
        display: show ? 'block' : ('none' as const)
      },
      searchArea: {},
      listContent: {}
    };
  }

  return {
    listArea: {
      flex: '1 1 auto',
      maxHeight: '100%',
      background: backgroundColor,
      padding: '24px',
      position: 'relative' as const,
      display: show ? 'flex' : ('none' as const),
      flexDirection: 'column' as const,
      minHeight: 0,
      boxSizing: 'border-box' as const
    },
    searchArea: {
      flexShrink: 0
    },
    listContent: {
      flex: '1 1 auto',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column' as const
    }
  };
};

export default ListAreaShortDistanceContainer;
